import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { getConfig, IClusterConfig, IConfig, ILayerState, ITooltipTemplate, IVectorLayerStyles } from "../Map/config";
import { convertToString } from "../Map/Coordinates/CoordinateUtils";
import { useAppContext } from "./AppContext";
import { DrawType, Widget } from "../Map/enums";
import { Feature } from "ol";
import { IEsriFieldInfo } from "../Map/interfaces";
import { IdentifyResultsArray } from "../Map/Identify/identifyUtils";
import { getUrlParam } from "../Map/utils";
import { GeoJSON } from "ol/format";
import { getDrawingLayer } from "../Map/mapFunctions";
import { IWmsState } from "./WmsContext";

export interface IFeaturesPopup {
    coordinates: number[];
    results: IdentifyResultsArray;
    hideArrow?: boolean;
    hideHighLight?: boolean;
}

export interface IHoveredCluster {
    feature: Feature;
    style: IVectorLayerStyles;
    clusterConfig: IClusterConfig;
}
export interface IHoveredFeature {
    feature: Feature;
    evt?: any;
    tooltipTemplate?: ITooltipTemplate;
    fieldInfo?: IEsriFieldInfo[];
}

export interface IMapContext {
    widget: Widget;
    setWidget: Dispatch<SetStateAction<Widget>>;
    drawingMenuVisible: boolean;
    setDrawingMenuVisible: Dispatch<SetStateAction<boolean>>;
    toggleDrawingMenuVisible: () => void;
    drawingInfoVisible: boolean;
    setDrawingInfoVisible: Dispatch<SetStateAction<boolean>>;
    configA: any;
    setConfigA: Dispatch<SetStateAction<any>>;
    layerStates: ILayerState[];
    setLayerStates: Dispatch<SetStateAction<ILayerState[]>>;
    wmsState: IWmsState[];
    setWmsState: Dispatch<SetStateAction<IWmsState[]>>;
    baseMap: string;
    setBaseMap: Dispatch<SetStateAction<string>>;
    coordsExpanded: boolean;
    setCoordsExpanded: Dispatch<SetStateAction<boolean>>;
    formatType: string;
    setFormatType: Dispatch<SetStateAction<string>>;
    getCurrentFormatCoords: (coords: number[]) => string;
    featuresPopup: IFeaturesPopup;
    setFeaturesPopup: Dispatch<SetStateAction<IFeaturesPopup>>;
    printFrame: boolean;
    setPrintFrame: Dispatch<SetStateAction<boolean>>;
    drawType: DrawType;
    setDrawType: Dispatch<SetStateAction<DrawType>>;
    drawnFeatures: Feature[];
    setDrawnFeatures: Dispatch<SetStateAction<Feature[]>>;
    saveDrawnFeatures: () => void;
    hoveredCluster: IHoveredCluster;
    setHoveredCluster: Dispatch<SetStateAction<IHoveredCluster>>;
    hoveredFeature: IHoveredFeature;
    setHoveredFeature: Dispatch<SetStateAction<IHoveredFeature>>;
    placingPin: boolean;
    setPlacing: Dispatch<SetStateAction<boolean>>;
    identifyCoordinates: { timestamp: number; coordinates: number[] };
    setIdentifyCoordinates: Dispatch<SetStateAction<{ timestamp: number; coordinates: number[] }>>;
    openDrawnFeatures: () => void;
}

const geojsonFormat = new GeoJSON();

const appid = encodeURI(window.location.host + window.location.pathname);
const storageName = "GeoLiteDrawings-" + appid;

const getStoredFeatures = () => {
    const storedFeatures = localStorage.getItem(storageName);
    const features = storedFeatures && geojsonFormat.readFeatures(storedFeatures);
    return features?.length ? features : [];
};

const MapContext = createContext<IMapContext>(null);

export const useMapContext = (): IMapContext => useContext(MapContext);

export const MapContextProvider: FC = (props: React.PropsWithChildren<IMapContext>) => {
    const { isIFrame, isMobile } = useAppContext();

    const [widget, setWidget] = useState<Widget>();
    const [drawingMenuVisible, setDrawingMenuVisible] = useState<boolean>(true);
    const [drawingInfoVisible, setDrawingInfoVisible] = useState<boolean>(false);
    const [configA, setConfigA] = useState<IConfig>();
    const [layerStates, setLayerStates] = useState<any[]>([]);
    const [wmsState, setWmsState] = useState<IWmsState[]>([]);
    const [baseMap, setBaseMap] = useState<string>();
    const [coordsExpanded, setCoordsExpanded] = useState<boolean>(!isIFrame && !isMobile);
    const [formatType, setFormatType] = useState("LKS");
    const [featuresPopup, setFeaturesPopup] = useState<IFeaturesPopup>();
    const [printFrame, setPrintFrame] = useState<boolean>(false);
    const [drawType, setDrawType] = useState<DrawType>();
    const [drawnFeatures, setDrawnFeatures] = useState<Feature[]>(getStoredFeatures());
    const [hoveredCluster, setHoveredCluster] = useState<IHoveredCluster>(null);
    const [hoveredFeature, setHoveredFeature] = useState<IHoveredFeature>(null);
    const [placingPin, setPlacing] = useState<boolean>(false);
    const [identifyCoordinates, setIdentifyCoordinates] = useState<{ timestamp: number; coordinates: number[] }>();

    const widgetRef = useRef<Widget>(widget);
    const drawingMenuVisibleRef = useRef<boolean>(true);

    useEffect(() => {
        getConfig().then((c) => {
            setConfigA(c);
            setBaseMap(c.basemaps[0].id);
        });
    }, []);

    useEffect(() => {
        if (!configA) {
            return;
        }

        const pos = getUrlParam("pos");
        const posData = pos && pos.split(",").map(Number);

        if (posData?.length === 2) {
            const hasConversionWidget =
                configA?.widgets?.CoordinateConversion || configA?.widgets?.CoordinateConversion === undefined;
            if (hasConversionWidget) {
                setWidget(Widget.Convert);
                setPlacing(true);
            }
        }

        if (drawnFeatures?.length) {
            document.addEventListener(
                "rendercomplete",
                () => {
                    getDrawingLayer().getSource().addFeatures(drawnFeatures);
                    // open drawing widget only if no Pos params (pin on map)
                    if (posData?.length !== 2) {
                        setWidget(Widget.Draw);
                        openDrawnFeatures();
                    }
                },
                { once: true }
            );
        }
    }, [configA]);

    useEffect(() => {
        widget !== Widget.Print && setPrintFrame(false);
        widget !== Widget.Print && deselectDrawnFeatures();
        widget === Widget.DataDownload && window.open(configA.dataDownloadUrl, "_blank").focus();
        widget === Widget.Help && window.open(configA.customerRow?.helpLink, "_blank").focus();
        widget !== Widget.Convert ? setPlacing(false) : setPlacing(true);
    }, [widget]);

    useEffect(() => saveDrawnFeatures(), [drawnFeatures]);

    const toggleDrawingMenuVisible = () => {
        drawingMenuVisibleRef.current = !drawingMenuVisibleRef.current;
        setDrawingMenuVisible(drawingMenuVisibleRef.current);
    };

    const getCurrentFormatCoords = useCallback(
        (coords: number[]) => {
            let coordinates = convertToString(formatType, coords);
            if (formatType === "LKS" || formatType === "LKSGEO") {
                const numbers = coordinates.split(",");
                coordinates = numbers[0] + " m, " + numbers[1] + " m";
            }
            return coordinates;
        },
        [formatType]
    );

    const prepareToSetWidget = (w: Widget) => {
        widgetRef.current = widgetRef.current === w ? null : w;
        setWidget(widgetRef.current);
    };

    const deselectDrawnFeatures = useCallback(
        () => drawnFeatures.length && drawnFeatures.map((f) => f.setStyle(undefined)),
        [drawnFeatures]
    );

    const saveDrawnFeatures = useCallback(
        () => localStorage.setItem(storageName, geojsonFormat.writeFeatures(drawnFeatures)),
        [drawnFeatures]
    );

    const openDrawnFeatures = () => {
        const drawingAccordion = document.getElementsByClassName("drawing-accordion")[0];
        if (drawingAccordion && drawingAccordion.clientHeight < 50) {
            const header = drawingAccordion && drawingAccordion.getElementsByClassName("accordion-item-header")[0];
            header && (header as HTMLElement).click();
        }
    };

    const values = {
        widget,
        setWidget: prepareToSetWidget,
        drawingMenuVisible,
        setDrawingMenuVisible,
        toggleDrawingMenuVisible,
        drawingInfoVisible,
        setDrawingInfoVisible,
        configA,
        setConfigA,
        layerStates,
        setLayerStates,
        wmsState,
        setWmsState,
        baseMap,
        setBaseMap,
        coordsExpanded,
        setCoordsExpanded,
        formatType,
        setFormatType,
        getCurrentFormatCoords,
        featuresPopup,
        setFeaturesPopup,
        printFrame,
        setPrintFrame,
        drawType,
        setDrawType,
        drawnFeatures,
        setDrawnFeatures,
        saveDrawnFeatures,
        hoveredCluster,
        setHoveredCluster,
        hoveredFeature,
        setHoveredFeature,
        placingPin,
        setPlacing,
        identifyCoordinates,
        setIdentifyCoordinates,
        openDrawnFeatures
    };

    return <MapContext.Provider value={values}>{props.children}</MapContext.Provider>;
};
